import {
  createSlice,
  createSelector,
  createEntityAdapter,
  PayloadAction,
  createAsyncThunk,
} from '@reduxjs/toolkit';

/**
 * WALLET SLICE - Redux Toolkit for Balance Management
 *
 * This slice focuses purely on balance data, keyed by 'address:network:walletType'.
 * Token metadata is handled by the separate tokenSlice.
 */

// Balance entity for adapter
interface BalanceEntity {
  id: string; // 'address:network:walletType'
  balance: number;
  uiString: string;
}

interface UpdateWalletBalancePayload {
  network: string;
  address: string; // Using address instead of symbol
  walletType: 'embedded' | 'external';
  balance?: number;
  uiString?: string;
}

// ── Adapter ──────────────────────────────────────────────────
// Balances (id = `${address}:${network}:${walletType}`)
const balancesAdapter = createEntityAdapter<BalanceEntity>();

// ── State ──────────────────────────────────────────────────
interface WalletUI {
  isLoading: boolean;
  error: string | null;
}

interface WalletState {
  balances: ReturnType<typeof balancesAdapter.getInitialState>;
  ui: WalletUI;
  refresh: {
    isRefreshing: boolean;
    lastRefreshTime: number;
  };
}

const initialState: WalletState = {
  balances: balancesAdapter.getInitialState(),
  ui: { isLoading: false, error: null },
  refresh: {
    isRefreshing: false,
    lastRefreshTime: 0,
  },
};

// ── Helper Functions ────────────────────────────────────────────────
const balanceKey = (
  address: string,
  network: string,
  walletType: 'embedded' | 'external',
) => `${address}:${network}:${walletType}`;

// ── Slice ──────────────────────────────────────────────────
const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // Balance operations

    resetWalletBalances: (state) => {
      state.balances = balancesAdapter.getInitialState();
    },

    upsertBalance: (state, action: PayloadAction<BalanceEntity>) => {
      balancesAdapter.upsertOne(state.balances, action.payload);
    },

    upsertBalances: (state, action: PayloadAction<BalanceEntity[]>) => {
      balancesAdapter.upsertMany(state.balances, action.payload);
    },

    updateWalletBalance: (
      state,
      action: PayloadAction<UpdateWalletBalancePayload>,
    ) => {
      const { network, address, walletType, balance, uiString } =
        action.payload;

      const balanceKeyStr = balanceKey(address, network, walletType);
      balancesAdapter.upsertOne(state.balances, {
        id: balanceKeyStr,
        balance: balance ?? 0,
        uiString: uiString ?? '0',
      });
    },

    updateWalletBalances: (
      state,
      action: PayloadAction<UpdateWalletBalancePayload[]>,
    ) => {
      if (action.payload.length === 0) {
        // If no balances, clear all
        balancesAdapter.removeAll(state.balances);
      } else {
        // Convert payload to BalanceEntity format and set all
        const newBalances: BalanceEntity[] = action.payload.map(
          ({ network, address, walletType, balance, uiString }) => ({
            id: balanceKey(address, network, walletType),
            balance: balance ?? 0,
            uiString: uiString ?? '0',
          }),
        );
        balancesAdapter.setAll(state.balances, newBalances);
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.ui.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.ui.error = action.payload;
    },

    clearExternalWalletBalances: (state) => {
      // Clear all external wallet balances by filtering out external balance entities
      const externalBalanceIds = Object.keys(state.balances.entities).filter(
        (id) => id.endsWith(':external'),
      );
      balancesAdapter.removeMany(state.balances, externalBalanceIds);
    },

    // Refresh state management
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refresh.isRefreshing = action.payload;
    },

    setLastRefreshTime: (state, action: PayloadAction<number>) => {
      state.refresh.lastRefreshTime = action.payload;
    },

    requestRefresh: (state) => {
      // Immediately set refreshing to true to prevent duplicate requests
      state.refresh.isRefreshing = true;
    },
  },
});

export const {
  upsertBalance,
  upsertBalances,
  resetWalletBalances,
  updateWalletBalance,
  updateWalletBalances,
  setLoading,
  setError,
  clearExternalWalletBalances,
  setRefreshing,
  setLastRefreshTime,
  requestRefresh,
} = walletSlice.actions;

// ── Auto-generated Entity Selectors ─────────────────────────
export const {
  selectById: selectBalanceEntity,
  selectAll: selectAllBalanceEntities,
  selectEntities: selectBalancesById,
  selectIds: selectBalanceIds,
  selectTotal: selectBalanceTotal,
} = balancesAdapter.getSelectors(
  (state: { wallet: WalletState }) => state.wallet.balances,
);

// ── Custom Balance Selectors ──────────────────────────────────

export const selectBalanceByAddress = createSelector(
  [
    (
      state: { wallet: WalletState },
      address: string,
      network: string,
      walletType: 'embedded' | 'external' = 'embedded',
    ) => ({
      balancesEntities: state?.wallet?.balances?.entities,
      address,
      network,
      walletType,
    }),
  ],
  ({ balancesEntities, address, network, walletType }) => {
    if (!balancesEntities) return 0;
    const balanceEntity =
      balancesEntities[balanceKey(address, network, walletType)];
    return balanceEntity?.balance ?? 0;
  },
);

export const selectCombinedBalanceByAddress = createSelector(
  [
    (state: { wallet: WalletState }, address: string, network: string) => ({
      balancesEntities: state.wallet.balances.entities,
      address,
      network,
    }),
  ],
  ({ balancesEntities, address, network }) => {
    const embeddedBalance =
      balancesEntities[balanceKey(address, network, 'embedded')]?.balance ?? 0;
    const externalBalance =
      balancesEntities[balanceKey(address, network, 'external')]?.balance ?? 0;
    return embeddedBalance + externalBalance;
  },
);

// Selector that returns all addresses with non-zero balances
export const selectAddressesWithBalance = createSelector(
  [(state: { wallet: WalletState }) => state.wallet.balances.entities],
  (balancesEntities) => {
    if (!balancesEntities) return [];

    const addressMap = new Map<string, number>();

    Object.entries(balancesEntities).forEach(([key, balanceEntity]) => {
      if (balanceEntity.balance > 0) {
        // Extract address from key format: 'address:network:walletType'
        const [address] = key.split(':');
        const currentBalance = addressMap.get(address) || 0;
        addressMap.set(address, currentBalance + balanceEntity.balance);
      }
    });

    return Array.from(addressMap.entries())
      .filter(([_, totalBalance]) => totalBalance > 0)
      .map(([address, totalBalance]) => ({ address, totalBalance }));
  },
);

// Selector that returns combined balances for all tokens across both wallet types
export const selectCombinedTokenBalances = createSelector(
  [(state: { wallet: WalletState }) => state.wallet.balances.entities],
  (balancesEntities) => {
    if (!balancesEntities) return [];

    const combinedBalances: Array<{
      address: string;
      network: string;
      balance: number;
      uiString: string;
    }> = [];

    // Group balances by address and network
    const balanceMap = new Map<
      string,
      {
        embedded: number;
        external: number;
        network: string;
      }
    >();

    Object.entries(balancesEntities).forEach(([key, balanceEntity]) => {
      if (balanceEntity.balance > 0) {
        // Extract address, network, and walletType from key format: 'address:network:walletType'
        const [address, network, walletType] = key.split(':');
        const mapKey = `${address}:${network}`;

        const current = balanceMap.get(mapKey) || {
          embedded: 0,
          external: 0,
          network,
        };

        if (walletType === 'embedded') {
          current.embedded = balanceEntity.balance;
        } else if (walletType === 'external') {
          current.external = balanceEntity.balance;
        }

        balanceMap.set(mapKey, current);
      }
    });

    // Convert to combined balance objects
    balanceMap.forEach(({ embedded, external, network }, key) => {
      const [address] = key.split(':');
      const totalBalance = embedded + external;

      if (totalBalance > 0) {
        combinedBalances.push({
          address,
          network,
          balance: totalBalance,
          uiString: totalBalance.toString(),
        });
      }
    });

    return combinedBalances;
  },
);

// ── Backwards Compatible Selectors ──────────────────────────
// These selectors maintain backwards compatibility by accepting addresses instead of symbols

export const selectTokenBalance =
  (network: string, address: string) => (state: { wallet: WalletState }) => {
    // Use address-based balance lookup directly
    const balancesEntities = state.wallet.balances.entities;
    const embeddedBalance =
      balancesEntities[balanceKey(address, network, 'embedded')]?.balance ?? 0;
    const externalBalance =
      balancesEntities[balanceKey(address, network, 'external')]?.balance ?? 0;
    return embeddedBalance + externalBalance;
  };

export const selectWalletTokenBalance = createSelector(
  [
    (
      state: { wallet: WalletState },
      network: string,
      address: string,
      walletType: 'embedded' | 'external',
    ) => {
      return {
        balancesEntities: state?.wallet?.balances?.entities,
        address,
        network,
        walletType,
      };
    },
  ],
  ({ balancesEntities, address, network, walletType }) => {
    if (!balancesEntities || !address) return 0;
    const balanceEntity =
      balancesEntities[balanceKey(address, network, walletType)];
    return balanceEntity?.balance ?? 0;
  },
);

export const selectSolanaUSDCBalance = (state: { wallet: WalletState }) => {
  // Use hardcoded USDC address for Solana
  const usdcAddress = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

  const embedded =
    selectBalanceEntity(state, balanceKey(usdcAddress, 'solana', 'embedded'))
      ?.balance ?? 0;
  const external =
    selectBalanceEntity(state, balanceKey(usdcAddress, 'solana', 'external'))
      ?.balance ?? 0;
  return embedded + external;
};

// ── Refresh State Selectors ──────────────────────────────────
export const selectIsRefreshing = (state: { wallet: WalletState }) =>
  state.wallet.refresh.isRefreshing;

export const selectLastRefreshTime = (state: { wallet: WalletState }) =>
  state.wallet.refresh.lastRefreshTime;

// Selector that returns all addresses with non-zero balances
export default walletSlice.reducer;
